/* exposureLicenseGate.js */

// Shared TAI-license gate for the two exposure entry points: the Qualify CVE Exposure action
// on the Devices grid and the Exposure tab in the device editor. Both hide the same feature
// for the same reason, and the interesting part is what happens when the license cannot be
// read at all — which is why it lives here as a plain module with injected collaborators
// rather than inline in two DI-heavy component classes where it could never be tested.
//
// Two properties this must hold, both of them load-bearing:
//
//   1. It ALWAYS resolves. Callers attach it as a side promise next to their own work; a
//      rejection here must never abort a device load or an explorer chain.
//   2. It fails CLOSED. If the license cannot be determined the feature is hidden, matching
//      ControlAppNavAddon._checkTAILicense and LicenseRestrictionAddon.isLicenseAvailable,
//      which both read "unknown" as "not licensed".
//
// The timeout is not belt-and-braces. whenLicenseFeedIsReady() resolves immediately when the
// feed is ready, but otherwise returns the feed's whenInitialized() promise, which on a dead
// feed simply never settles — no resolve, no reject. Without racing it, a bare catch would
// never run and the feature would stay visible forever. An organization switch clears the feed
// and the ready flag, so that window is reachable in normal use, not only during an outage.

const TAI_LICENSE = "TAI";
const LICENSE_READY_TIMEOUT_MS = 5000;

// Distinguishes "the race was won by the timer" from any value the feed might resolve with.
const TIMED_OUT = {};

/**
 * Hides the feature without ever letting an error escape. A throwing hide() must not turn
 * into a rejected gate promise.
 * @param {Function} hide Caller-supplied hide callback.
 * @param {Object} [logger] Optional logger.
 * @param {String} reason Why the feature is being hidden.
 * @param {*} [error] Underlying error, when there is one.
 * @returns {void}
 */
function hideSafely(hide, logger, reason, error) {
    if (logger && logger.warn) {
        logger.warn({error}, "Exposure license gate: " + reason + " — hiding exposure.");
    }
    if (typeof hide !== "function") {
        return;
    }
    try {
        hide();
    } catch (err) {
        if (logger && logger.warn) {
            logger.warn({error: err}, "Exposure license gate: hide() threw.");
        }
    }
}

/**
 * Hides the exposure feature unless the tenant holds an active TAI license.
 * @param {Object} opts Injected collaborators.
 * @param {Object} opts.licenseRestriction The $teve.licenseRestriction service.
 * @param {Function} opts.hide Called when exposure must be hidden.
 * @param {Object} [opts.logger] Optional logger for the fail-closed paths.
 * @param {Number} [opts.timeoutMs] Override the license-feed wait (tests use a short one).
 * @returns {Promise<{licensed: boolean}>} Always resolves, never rejects.
 */
function applyExposureLicenseGate(opts) {
    const licenseRestriction = opts.licenseRestriction;
    const hide = opts.hide;
    const logger = opts.logger;
    const timeoutMs = typeof opts.timeoutMs === "number" ? opts.timeoutMs : LICENSE_READY_TIMEOUT_MS;
    let licensed = false;

    if (!licenseRestriction) {
        hideSafely(hide, logger, "no license service available");
        return Promise.resolve({licensed});
    }

    let timer;

    // Promise.resolve().then() so a synchronous throw from whenLicenseFeedIsReady() lands in
    // the catch below rather than escaping to the caller.
    const ready = Promise.resolve().then(() => licenseRestriction.whenLicenseFeedIsReady());
    const expiry = new Promise((resolve) => {
        timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs);
    });

    return Promise.race([ready, expiry])
        .then((outcome) => {
            if (outcome === TIMED_OUT) {
                hideSafely(
                    hide,
                    logger,
                    "license feed did not settle within " + timeoutMs + "ms"
                );
                return;
            }
            if (!licenseRestriction.checkLicense(TAI_LICENSE)) {
                hideSafely(hide, logger, "tenant has no TAI license");
                return;
            }
            licensed = true;
        })
        .catch((error) => {
            hideSafely(hide, logger, "license lookup failed", error);
        })
        .then(() => {
            clearTimeout(timer);
            return {licensed};
        });
}

module.exports = {applyExposureLicenseGate, TAI_LICENSE, LICENSE_READY_TIMEOUT_MS};
